// src/oura/components/newscan/InlineText.tsx
//
// A value that turns into a field in place: click (or Enter) to edit, Enter
// or leaving the field confirms, Esc puts the old value back. The value wraps
// onto a second line rather than hiding its end.

import { useId, useRef, useState, type KeyboardEvent } from "react";
import { cn } from "@oura/lib/cn";
import { FOCUS_RING, TYPE } from "@oura/ui";
import { inputClass } from "@oura/onboarding/messageText";

export interface InlineTextProps {
    value: string;
    label: string;
    /** Shown in blue when there is no value yet. */
    empty: string;
    onCommit(next: string): void;
    disabled?: boolean;
    strong?: boolean;
    /** An empty field puts the old value back instead of saving nothing. */
    required?: boolean;
}

export function InlineText({ value, label, empty, onCommit, disabled, strong, required }: InlineTextProps) {
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState(value);
    const fieldId = useId();
    const buttonRef = useRef<HTMLButtonElement>(null);

    // The Enter that closes the field must not reopen it through the value it hands focus to.
    const closedAt = useRef(0);
    const open = () => {
        if (Date.now() - closedAt.current < 300) return;
        setText(value);
        setEditing(true);
    };
    const finish = (save: boolean, refocus: boolean) => {
        closedAt.current = Date.now();
        setEditing(false);
        const next = text.trim().replace(/\s+/g, " ");
        if (save && next !== value && (next || !required)) onCommit(next);
        // Enter and Esc hand focus back to the value; leaving the field keeps it where it went.
        if (refocus) requestAnimationFrame(() => buttonRef.current?.focus());
    };
    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.nativeEvent.isComposing) return;
        if (e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();
            finish(true, true);
        } else if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            finish(false, true);
        }
    };

    if (editing) {
        // The width sits on the wrapper: inputClass() keeps its own height and full width.
        return (
            <span className="inline-flex w-64 min-w-0 max-w-full">
                <label htmlFor={fieldId} className="sr-only">
                    {label}
                </label>
                <input
                    id={fieldId}
                    type="text"
                    autoFocus
                    value={text}
                    autoComplete="off"
                    enterKeyHint="done"
                    onChange={(e) => setText(e.target.value)}
                    // Opens with the old value selected: typing replaces it rather than appending.
                    onFocus={(e) => e.currentTarget.select()}
                    onKeyDown={onKeyDown}
                    onBlur={() => finish(true, false)}
                    className={cn(inputClass(), strong && "font-medium")}
                />
            </span>
        );
    }

    const isEmpty = !value.trim();
    return (
        <button
            ref={buttonRef}
            type="button"
            disabled={disabled}
            onClick={open}
            aria-label={isEmpty ? empty : `${label}: ${value}. Change`}
            className={cn(
                TYPE.tb,
                "relative -mx-1.5 inline-flex min-h-8 min-w-0 max-w-full items-center gap-1.5 rounded-lg px-1.5 text-left",
                "after:absolute after:-inset-y-1.5 after:inset-x-0 after:content-[''] motion-safe:transition-colors",
                "enabled:hover:bg-gray-50 disabled:cursor-default",
                FOCUS_RING,
                isEmpty ? "text-blue-600" : strong ? "font-medium text-gray-900" : "text-gray-500",
            )}
        >
            <span className="min-w-0 break-words">{isEmpty ? empty : value}</span>
        </button>
    );
}
